import { createReducer, createActions } from 'reduxsauce';
import Immutable from 'seamless-immutable';

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  initSession:             ['sessionId'],
  sendMessageRequest:      ['campaignId', 'userId', 'text'],
  sendMessageSuccess:      ['response'],
  sendMessageFailure:      ['errors'],
  confirmProposalRequest:  ['proposalAction', 'confirmed', 'checkedIds'],
  confirmProposalSuccess:  ['response'],
  confirmProposalFailure:  ['errors'],
  resetSessionRequest:     [],
  resetSessionSuccess:     ['sessionId'],
  resetSessionFailure:     ['errors'],
  clearMessages:           [],
  clearChatbot:            [],
  openWithGreeting:        ['text'],
  autoOpenHandled:         [],
});

export const ChatbotTypes = Types;
export default Creators;

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  sessionId:          null,
  messages:           [],
  currentProposal:    null,
  currentProposalId:  null,
  currentAction:      null,
  suggestReset:       false,
  sendingMessage:     false,
  confirmingProposal: false,
  resettingSession:   false,
  error:              null,
  autoOpenRequested:  false,
});

/* ------------- Helpers ------------- */

const msgId = () => `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

// Mark the message carrying this proposal as resolved so its card renders
// disabled. `outcome` ('confirmed' | 'rejected') is kept for bookkeeping.
const resolveProposalMessage = (messages, proposalId, outcome) =>
  messages.map(m =>
    m.proposalId && m.proposalId === proposalId
      ? { ...m, resolved: true, proposalOutcome: outcome }
      : m
  );

// Append an assistant response (answer / action_proposal / map_focus /
// confirmation_feedback) to the timeline. Shared by /chat/message and
// /chat/confirm: an action_proposal (also chained from /chat/confirm) becomes
// the active proposal; otherwise the pointers are left as given in `extra`.
const appendAssistantResponse = (state, response, extra) => {
  const { response_type, message, proposal_id, proposal, suggest_reset } = response;
  const isProposal = response_type === 'action_proposal';

  const assistantMsg = {
    id: msgId(),
    role: 'assistant',
    text: message || '',
    responseType: response_type,
    // Embed the proposal in the message so it lives in the timeline like any
    // other message and can later be shown disabled (once confirmed/rejected).
    proposal: isProposal ? proposal : null,
    proposalId: isProposal ? proposal_id : null,
    resolved: false,
  };

  const update = {
    ...extra,
    suggestReset: !!suggest_reset,
    messages:     [...state.messages, assistantMsg],
  };

  if (isProposal) {
    update.currentProposal   = proposal;
    update.currentProposalId = proposal_id;
    update.currentAction     = proposal ? proposal.action : null;
  }

  return state.merge(update);
};

/* ------------- Reducers ------------- */

const initSession = (state, { sessionId }) =>
  state.merge({ sessionId });

const sendMessageRequest = (state, { text }) =>
  state.merge({
    sendingMessage: true,
    error: null,
    messages: [...state.messages, { id: msgId(), role: 'user', text }],
  });

const sendMessageSuccess = (state, { response }) => {
  if (response.response_type === 'session_reset') {
    // The server already cleared the session. Wipe local history but keep a
    // system notice so the user understands the conversation was reset.
    return state.merge({
      sendingMessage: false,
      sessionId: response.session_id != null ? response.session_id : state.sessionId,
      messages: [{ id: msgId(), role: 'assistant', text: null, responseType: 'session_reset' }],
      currentProposal: null,
      currentProposalId: null,
      currentAction: null,
      suggestReset: false,
    });
  }

  return appendAssistantResponse(state, response, { sendingMessage: false });
};

const sendMessageFailure = (state, { errors }) =>
  state.merge({ sendingMessage: false, error: (errors && errors[0]) || 'Error' });

const confirmProposalRequest = (state, { confirmed }) =>
  state.merge({
    confirmingProposal: true,
    error: null,
    // Lock the proposal card as soon as the user acts on it: it stays in the
    // timeline but its buttons are disabled from here on.
    messages: resolveProposalMessage(
      state.messages,
      state.currentProposalId,
      confirmed ? 'confirmed' : 'rejected'
    ),
  });

// /chat/confirm may chain a new action_proposal (multi-change requests are
// proposed one at a time); confirmation_feedback / answer end the cycle.
const confirmProposalSuccess = (state, { response }) =>
  appendAssistantResponse(state, response, {
    confirmingProposal: false,
    currentProposal:    null,
    currentProposalId:  null,
    currentAction:      null,
  });

const confirmProposalFailure = (state, { errors }) =>
  state.merge({
    confirmingProposal: false,
    error: (errors && errors[0]) || 'Error',
    // The card was already locked in confirmProposalRequest; just clear the
    // active-proposal pointers so a consumed/expired proposal (e.g. 404) can't
    // be acted on again.
    currentProposal:   null,
    currentProposalId: null,
    currentAction:     null,
  });

const resetSessionRequest = state =>
  state.merge({ resettingSession: true });

const resetSessionSuccess = (state, { sessionId }) =>
  state.merge({
    resettingSession:   false,
    sessionId,
    messages:           [],
    currentProposal:    null,
    currentProposalId:  null,
    currentAction:      null,
    suggestReset:       false,
    error:              null,
  });

const resetSessionFailure = (state, { errors }) =>
  state.merge({ resettingSession: false, error: (errors && errors[0]) || 'Error' });

const clearMessages = state =>
  state.merge({
    messages:          [],
    currentProposal:   null,
    currentProposalId: null,
    currentAction:     null,
    suggestReset:      false,
  });

// Full local reset of the AI Planner: wipes the session, messages and any
// in-flight proposal. No /chat/reset call (server sessions expire on their own);
// a fresh session is created next time the chat is opened.
const clearChatbot = () => INITIAL_STATE;

const openWithGreeting = (state, { text }) =>
  state.merge({
    autoOpenRequested: true,
    messages: state.messages.length > 0
      ? state.messages
      : [{ id: msgId(), role: 'assistant', text, responseType: 'greeting', proposal: null, proposalId: null, resolved: false }],
  });

const autoOpenHandled = state =>
  state.merge({ autoOpenRequested: false });

/* ------------- Hookup ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.INIT_SESSION]:             initSession,
  [Types.SEND_MESSAGE_REQUEST]:     sendMessageRequest,
  [Types.SEND_MESSAGE_SUCCESS]:     sendMessageSuccess,
  [Types.SEND_MESSAGE_FAILURE]:     sendMessageFailure,
  [Types.CONFIRM_PROPOSAL_REQUEST]: confirmProposalRequest,
  [Types.CONFIRM_PROPOSAL_SUCCESS]: confirmProposalSuccess,
  [Types.CONFIRM_PROPOSAL_FAILURE]: confirmProposalFailure,
  [Types.RESET_SESSION_REQUEST]:    resetSessionRequest,
  [Types.RESET_SESSION_SUCCESS]:    resetSessionSuccess,
  [Types.RESET_SESSION_FAILURE]:    resetSessionFailure,
  [Types.CLEAR_MESSAGES]:           clearMessages,
  [Types.CLEAR_CHATBOT]:            clearChatbot,
  [Types.OPEN_WITH_GREETING]:       openWithGreeting,
  [Types.AUTO_OPEN_HANDLED]:        autoOpenHandled,
});
